import React, { FC, useEffect, useState } from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";

import GridContainer from "@insite/mobius/GridContainer";
import Button from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css, useTheme } from "styled-components";
import * as XLSX from "xlsx";

import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { connect } from "react-redux";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

const UPLOAD_TYPES = {
    cutoff: "CUTOFF_TRANSFER_MATRIX",
    UPLOAD2: "ALL_DIVISION_DELIVERY_MATRIX",
    UPLOAD3: "WAREHOUSE_PRIORITY_MATRIX",
} as const;

type UploadType = (typeof UPLOAD_TYPES)[keyof typeof UPLOAD_TYPES];

const TEMPLATE_HEADERS: Record<UploadType, string[]> = {
    CUTOFF_TRANSFER_MATRIX: ["FromCity", "CutoffTime", "ToCity", "TransitDays"],

    ALL_DIVISION_DELIVERY_MATRIX: [
        "BRANCH",
        "SHIP TO STATE",
        "SHIP TO CITY",
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "ROUTE NAME",
        "DELIVERY NOTES",
    ],

    WAREHOUSE_PRIORITY_MATRIX: ["Home_Warehouse", "Stock_Transfer_Warehouse", "Fulfillment_Priority"],
};

const COLUMN_COUNT: Record<UploadType, number> = {
    CUTOFF_TRANSFER_MATRIX: 4,
    ALL_DIVISION_DELIVERY_MATRIX: 10,
    WAREHOUSE_PRIORITY_MATRIX: 3,
};

const enum fields {
    tile1Title = "tile1Title",
    tile1Description = "tile1Description",
    tile1TemplateUrl = "tile1TemplateUrl",

    tile2Title = "tile2Title",
    tile2Description = "tile2Description",
    tile2TemplateUrl = "tile2TemplateUrl",

    tile3Title = "tile3Title",
    tile3Description = "tile3Description",
    tile3TemplateUrl = "tile3TemplateUrl",

    successMessage = "successMessage",
    errorMessage = "errorMessage",
}

interface Props extends WidgetProps {
    fields: {
        [fields.tile1Title]: string;
        [fields.tile1Description]: string;
        [fields.tile1TemplateUrl]: LinkFieldValue;

        [fields.tile2Title]: string;
        [fields.tile2Description]: string;
        [fields.tile2TemplateUrl]: LinkFieldValue;

        [fields.tile3Title]: string;
        [fields.tile3Description]: string;
        [fields.tile3TemplateUrl]: LinkFieldValue;

        [fields.successMessage]: string;
        [fields.errorMessage]: string;
    };
}

interface SessionProps {
    session: ReturnType<typeof getSession>;
}

interface TileConfig {
    title: string;
    description: string;
    templateUrl?: string;
    uploadType: UploadType;
}

interface SpreadsheetUploadStyles {
    wrapper?: InjectableCss;
    leftColumn?: InjectableCss;
    tile?: InjectableCss;
    tileActive?: InjectableCss;
    rightColumnOuter?: InjectableCss;
    rightColumnInner?: InjectableCss;
    actionRow?: InjectableCss;
    error?: InjectableCss;
}

const createStyles = (theme: any): SpreadsheetUploadStyles => ({
    wrapper: {
        css: css`
            max-width: 1280px;
            margin: 40px auto 80px;
            padding: 0 24px;
        `,
    },
    leftColumn: {
        css: css`
            width: 280px;
            min-width: 280px;
            border: 1px solid ${theme.colors.common.border};
            background: ${theme.colors.common.background};
            margin-right: 24px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 6px;
            @media (max-width: 768px) {
                width: 100%;
                min-width: 100%;
                margin-right: 0;
                justify-content: flex-start;
            }
        `,
    },
    tile: {
        css: css`
            height: 52px;
            padding: 0 22px;
            margin-bottom: 14px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
            border-radius: 26px;
            border: 1px solid ${theme.colors.common.border};
            background: ${theme.colors.common.background};
            color: ${theme.colors.text.main};
            font-family: ${theme.typography.body.fontFamily};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background: ${theme.colors.secondary.main};
                color: ${theme.colors.secondary.contrast};
                border-color: ${theme.colors.secondary.main};
            }
        `,
    },
    tileActive: {
        css: css`
            height: 52px;
            padding: 0 22px;
            margin-bottom: 14px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
            border-radius: 26px;
            border: 1px solid ${theme.colors.primary.main};
            background: ${theme.colors.primary.main};
            color: ${theme.colors.primary.contrast};
            font-weight: 700;
        `,
    },
    rightColumnOuter: {
        css: css`
            flex: 1;
            border: 1px solid ${theme.colors.common.border};
            background: ${theme.colors.common.background};
            padding: 40px 48px;
            @media (max-width: 768px) {
                width: 100%;
                padding: 24px 16px;
            }
        `,
    },
    rightColumnInner: {
        css: css`
            max-width: 900px;
        `,
    },
    actionRow: {
        css: css`
            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    error: {
        css: css`
            margin-top: 16px;
            color: ${theme.colors.danger.main};
            font-weight: 600;
        `,
    },
});

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state),
});

const SpreadsheetUploadMulti: FC<Props & SessionProps> = ({ fields, session }) => {
    const theme = useTheme();
    const styles = createStyles(theme);

    const [status, setStatus] = useState<string>("");
    const [fileInputKey, setFileInputKey] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);

    const tile1Link = useGetLink(fields.tile1TemplateUrl);
    const tile2Link = useGetLink(fields.tile2TemplateUrl);
    const tile3Link = useGetLink(fields.tile3TemplateUrl);

    const Message = () => (
        <StyledWrapper
            css={css`
                text-align: center;
                margin: 80px auto;
                max-width: 600px;
            `}
        >
            <Typography variant="h2">Please login to access the resource. Redirecting to Sign In…</Typography>
        </StyledWrapper>
    );

    const NotAuthorized = () => (
        <StyledWrapper
            css={css`
                text-align: center;
                margin: 80px auto;
                max-width: 600px;
            `}
        >
            <Typography variant="h2">Not Authorized</Typography>
            <Typography variant="p">Only Admin can upload Delivery Matrix files.</Typography>
        </StyledWrapper>
    );

    const notAuth = session && !session.isAuthenticated;

    useEffect(() => {
        if (!notAuth) return;

        const id = setTimeout(() => {
            const returnUrl = encodeURIComponent(window.location.pathname);
            window.location.replace(`/SignIn?returnUrl=${returnUrl}`);
        }, 500);

        return () => clearTimeout(id);
    }, [notAuth]);

    if (!session) return null;

    if (notAuth) return <Message />;

    const isAuthorised = String(session?.userRoles || "")
        .toLowerCase()
        .includes("fileupload");

    if (!isAuthorised) return <NotAuthorized />;

    const tiles: TileConfig[] = [
        {
            title: fields.tile1Title,
            description: fields.tile1Description,
            templateUrl: tile1Link.url,
            uploadType: UPLOAD_TYPES.cutoff,
        },
        {
            title: fields.tile2Title,
            description: fields.tile2Description,
            templateUrl: tile2Link.url,
            uploadType: UPLOAD_TYPES.UPLOAD2,
        },
        {
            title: fields.tile3Title,
            description: fields.tile3Description,
            templateUrl: tile3Link.url,
            uploadType: UPLOAD_TYPES.UPLOAD3,
        },
    ].filter(t => t.title);

    const activeTile = tiles[selectedIndex];

    const validateTemplate = (headers: string[]) => {
        const expected = TEMPLATE_HEADERS[activeTile.uploadType];
        const normalized = headers.map(h => h.trim().toLowerCase());
        const expectedNormalized = expected.map(h => h.toLowerCase());

        return normalized.length === expectedNormalized.length && expectedNormalized.every(h => normalized.includes(h));
    };

    const onUpload = async () => {
        if (!file) return;

        setStatus("Processing...");
        setError(null);
        setUploading(true);

        const reader = new FileReader();

        reader.onload = async e => {
            const wb = XLSX.read(e.target?.result as string, { type: "binary" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

            const headers = (data[0] || []).map(h => String(h));

            if (!validateTemplate(headers)) {
                setError(
                    `Invalid template for "${activeTile.title}". Expected columns: ${TEMPLATE_HEADERS[
                        activeTile.uploadType
                    ].join(", ")}`,
                );
                setStatus("");
                setUploading(false);
                return;
            }

            const payload = data.slice(1).map(row => {
                const record: any = {
                    uploadType: activeTile.uploadType,
                    fileName: file.name,
                };

                const count = COLUMN_COUNT[activeTile.uploadType];
                for (let i = 0; i < count; i++) {
                    record[`column${i + 1}`] = row[i] ?? "";
                }

                return record;
            });

            if (payload.length === 0) {
                setStatus("No valid rows found.");
                setUploading(false);
                return;
            }

            setStatus("Uploading...");

            try {
                const res = await fetch("/api/v1/spreadsheetupload/upload", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const result = await res.json();

                if (res.ok && result === true) {
                    const message =
                        fields.successMessage?.replace("{count}", payload.length.toString()) ||
                        `Upload successful. Imported ${payload.length} rows.`;

                    setStatus(message);
                    setFile(null);
                    setFileInputKey(prev => prev + 1);
                } else {
                    setError(fields.errorMessage || "Upload failed. Server rejected the file.");
                }
            } catch {
                setError("Unable to reach server.");
            } finally {
                setUploading(false);
            }
        };

        reader.readAsBinaryString(file);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer
                css={css`
                    display: flex;
                    gap: 32px;
                `}
            >
                {/* LEFT */}
                <StyledWrapper {...styles.leftColumn}>
                    {tiles.map((tile, index) => (
                        <StyledWrapper
                            key={index}
                            {...styles.tile}
                            {...(index === selectedIndex ? styles.tileActive : {})}
                            onClick={() => {
                                setSelectedIndex(index);
                                setFile(null);
                                setError(null);
                                setStatus("");
                                setFileInputKey(prev => prev + 1);
                            }}
                        >
                            {tile.title}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>

                {/* RIGHT */}
                <StyledWrapper {...styles.rightColumnOuter}>
                    <StyledWrapper {...styles.rightColumnInner}>
                        <Typography variant="h2">{activeTile.title}</Typography>
                        <Typography variant="p">{activeTile.description}</Typography>

                        <StyledWrapper {...styles.actionRow}>
                            <input
                                key={fileInputKey}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={e => setFile(e.target.files?.[0] ?? null)}
                            />

                            <Button variant="primary" disabled={!file || uploading} onClick={onUpload}>
                                {uploading ? "Uploading..." : "Upload"}
                            </Button>

                            {activeTile.templateUrl && (
                                <Button
                                    variant="secondary"
                                    onClick={() => window.open(activeTile.templateUrl!, "_blank")}
                                >
                                    Download Template
                                </Button>
                            )}

                            {status && (
                                <Typography
                                    variant="p"
                                    css={css`
                                        margin-top: 12px;
                                    `}
                                >
                                    {status}
                                </Typography>
                            )}
                        </StyledWrapper>

                        {error && <StyledWrapper {...styles.error}>{error}</StyledWrapper>}
                    </StyledWrapper>
                </StyledWrapper>
            </GridContainer>
        </StyledWrapper>
    );
};

const ConnectedSpreadsheetUploadMulti = connect(mapStateToProps)(SpreadsheetUploadMulti);

const widgetModule: WidgetModule = {
    component: ConnectedSpreadsheetUploadMulti,
    definition: {
        group: "Common",
        displayName: "Spreadsheet Upload – Multi Tile",
        fieldDefinitions: [
            {
                name: fields.tile1Title,
                displayName: "Tile 1 Title",
                editorTemplate: "TextField",
                defaultValue: "Cutoff Transfer Matrix Upload",
                fieldType: "General",
            },
            {
                name: fields.tile1Description,
                displayName: "Tile 1 Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tile1TemplateUrl,
                displayName: "Tile 1 Template URL",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Url" },
                fieldType: "General",
            },

            {
                name: fields.tile2Title,
                displayName: "Tile 2 Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tile2Description,
                displayName: "Tile 2 Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tile2TemplateUrl,
                displayName: "Tile 2 Template URL",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Url" },
                fieldType: "General",
            },

            {
                name: fields.tile3Title,
                displayName: "Tile 3 Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tile3Description,
                displayName: "Tile 3 Description",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.tile3TemplateUrl,
                displayName: "Tile 3 Template URL",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Url" },
                fieldType: "General",
            },
            {
                name: fields.successMessage,
                displayName: "Upload Success Message",
                editorTemplate: "MultilineTextField",
                defaultValue: "Upload successful. Imported {count} rows.",
                fieldType: "General",
            },
            {
                name: fields.errorMessage,
                displayName: "Upload Error Message",
                editorTemplate: "MultilineTextField",
                defaultValue: "Upload failed. Server rejected the file.",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
